import { kebabCase } from 'lodash-es';
import React from 'react';

import { HomepageHeroImages, LandingPageHeroQuestion, useMultipleVariantData } from '@pflegia/feature-flags';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { useGetCountryDetails } from '@pflegia/ui/hooks/use-get-country-details';
import AvailableJobs from '@pflegia/ui/shared/available-jobs';
import { getButtonClassName } from '@pflegia/ui/shared/button';
import ButtonLink from '@pflegia/ui/shared/button-link/ButtonLink';
import ContainerSection from '@pflegia/ui/shared/container-section';
import GuestHeroImage from '@pflegia/ui/shared/guest-hero-image';
import QuestionsAndAnswers from '@pflegia/ui/shared/landing-page/questions-and-answers/QuestionsAndAnswers';
import { TestimonialSection } from '@pflegia/ui/shared/testimonial';

import PositionCluster from '@domains/landing-page/components/position-cluster/PositionCluster';
import SalaryCalculatorTeaser from '@domains/landing-page/components/salary-calculator-teaser/SalaryCalculatorTeaser';
import TopCompany from '@domains/landing-page/components/top-company/TopCompany';

import { ReactComponent as AustriaFlag } from '@public/assets/austria-flag.svg';

import { FUNNEL, JOBS, PFLEGIA_FRIENDS_PROGRAM } from '@shared/application/constants';
import { RegisterButtonTrackingIds } from '@shared/application/marketing/events/register-button';
import { EMPLOYER_COUNT } from '@shared/constants/counts';

import Advantages from './components/advantages';
import BundeslandCluster from './components/bundesland-cluster/BundeslandCluster';
import CityCluster from './components/city-cluster/CityCluster';
import DemandGapCluster from './components/city-cluster/DemandGapCluster';
import EmployerTypeButtons from './components/employer-type-buttons';
import Employers from './components/employers/Employers';
import GuestHeader from './components/header/GuestHeader';
import Headline from './components/headline/Headline';
import HowItWorks from './components/how-it-works/HowItWorks';
import PflegiaHowItWorksCta from './components/how-it-works/PflegiaHowItWorksCTA';
import JobSearch from './components/job-search';
import NursingAreaButtons from './components/nursing-area-buttons';
import OfferedValues from './components/offered-values';
import PflegiaFriends from './components/pflegia-friends';
import PflegiaFriendsRedirect from './components/pflegia-friends-redirect/PflegiaFriendsRedirect.component';
import SearchFlow from './components/search-flow';
import SelectEmploymentType from './components/select-employment-type';
import TopBar from './components/top-bar/TopBar';
import {
  ADVANTAGES,
  CARE_TYPES,
  EMPLOYER_LOGOS,
  HOW_IT_WORKS_VIDEO,
  OFFERED_VALUES,
  PFLEGIA_FAQ,
  SEARCH_FLOW_STEPS,
  TESTIMONIALS,
} from './constants/guest-landing-page';
import useLandingPageSharedFunctions from './hooks/use-landing-page-shared-functions';

const GuestLandingPage: React.FC = () => {
  const { t, getTestimonialPropsFromName, signupButtonClickTrackEventRegistration, headerRef, headerInView } =
    useLandingPageSharedFunctions();
  const { sourceDomainName, isPraxia } = useFlavorFromRoute();
  const { name: countryName } = useGetCountryDetails();
  const { payload: heroImage } = useMultipleVariantData<HomepageHeroImages.PayloadType>(HomepageHeroImages.Name);
  const { payload: landingPageHeroQuestion } = useMultipleVariantData<LandingPageHeroQuestion.PayloadType>(LandingPageHeroQuestion.Name);
  const isNursingArea = landingPageHeroQuestion === LandingPageHeroQuestion.PayloadType.NursingArea;

  const flag =
    countryName === 'Austria' ? (
      <AustriaFlag className='absolute top-[15%] right-2 h-10 w-15 shadow-sm lg:h-15 lg:w-22.5' title='Austria' />
    ) : undefined;

  return (
    <>
      <TopBar />
      <main className='mb-20 flex flex-col gap-24 md:gap-40'>
        <div className='bg-primary-100'>
          <ContainerSection>
            <div ref={headerRef}>
              <GuestHeader
                heroButtons={isNursingArea ? <NursingAreaButtons /> : <EmployerTypeButtons />}
                headerImage={<GuestHeroImage alt={t('header.guestHeroImage')} src={heroImage} flag={flag} className='lg:top-3' />}
                funnelLabel={isNursingArea ? 'funnelLabel' : 'funnelLabelEmployeeType'}
              />
            </div>
          </ContainerSection>
          <TopCompany />
        </div>
        <ContainerSection>
          <Advantages advantages={ADVANTAGES} maxCols={4} titleTranslationKey='whyPflegiaGuest' altTextShown />
        </ContainerSection>
        <TestimonialSection {...getTestimonialPropsFromName(TESTIMONIALS[0])} />
        {!isPraxia && (
          <>
            <ContainerSection>
              <SalaryCalculatorTeaser />
            </ContainerSection>
            <ContainerSection>
              <DemandGapCluster />
            </ContainerSection>
          </>
        )}
        <ContainerSection>
          <AvailableJobs
            altTextShown
            items={CARE_TYPES}
            headline={
              <Headline
                i18nKey='landingPage:availableJobs.title'
                subTitle='landingPage:availableJobs.subtitle'
                subTitleValues={{ employerCount: EMPLOYER_COUNT }}
              />
            }
            itemLink={item => `${JOBS}/${kebabCase(t(`candidateFields:typesOfWork.${item}`))}`}
          />
          {!isPraxia && <PositionCluster variant='chips' className='mt-24' />}
          <JobSearch className='mt-40' />
        </ContainerSection>
        <TestimonialSection {...getTestimonialPropsFromName(TESTIMONIALS[1])} imgOnRight />
        <ContainerSection>
          <SelectEmploymentType />
        </ContainerSection>
        {!isPraxia && (
          <>
            <ContainerSection className='overflow-x-hidden lg:overflow-visible'>
              <CityCluster subtitleKey='landingPage:cityCluster.subtitle' />
            </ContainerSection>
            <ContainerSection>
              <BundeslandCluster />
            </ContainerSection>
          </>
        )}
        <ContainerSection>
          <HowItWorks video={HOW_IT_WORKS_VIDEO} alt={t('howItWorks.altText')} />
        </ContainerSection>
        <ContainerSection>
          <PflegiaHowItWorksCta
            registerButtonId={RegisterButtonTrackingIds.LandingPageHowItWorks}
            altDesktopImage={t('howItWorks.altTextCtaDesktopLandingPage')}
            altMobileImage={t('howItWorks.altTextCtaMobileLandingPage')}
          />
        </ContainerSection>
        <TestimonialSection {...getTestimonialPropsFromName(TESTIMONIALS[2])} />
        <ContainerSection>
          <OfferedValues values={OFFERED_VALUES} />
        </ContainerSection>
        {/* eslint-disable-next-line no-magic-numbers */}
        <TestimonialSection {...getTestimonialPropsFromName(TESTIMONIALS[3])} imgOnRight />
        <ContainerSection>
          <SearchFlow steps={SEARCH_FLOW_STEPS} />
        </ContainerSection>
        <ContainerSection>
          <PflegiaFriendsRedirect href={PFLEGIA_FRIENDS_PROGRAM} />
        </ContainerSection>
        {/* eslint-disable-next-line no-magic-numbers */}
        <TestimonialSection {...getTestimonialPropsFromName(TESTIMONIALS[4])} />
        <ContainerSection>
          <Employers employerLogos={EMPLOYER_LOGOS} />
        </ContainerSection>
        {/* eslint-disable-next-line no-magic-numbers */}
        <TestimonialSection {...getTestimonialPropsFromName(TESTIMONIALS[5])} imgOnRight />
        {!isPraxia && (
          <ContainerSection>
            <PositionCluster variant='cards' />
          </ContainerSection>
        )}
        <ContainerSection>
          <QuestionsAndAnswers faqs={PFLEGIA_FAQ} sourceDomainName={sourceDomainName} />
        </ContainerSection>
        {!headerInView && (
          <ButtonLink
            href={FUNNEL}
            className={getButtonClassName('primary', 'fixed right-3 bottom-3 z-20 animate-[fade-in_300ms_forwards] cursor-pointer')}
            onClick={() => signupButtonClickTrackEventRegistration(RegisterButtonTrackingIds.LandingPageRightBottomCornerId)}>
            {t('findAJob')}
          </ButtonLink>
        )}
      </main>
      <PflegiaFriends />
    </>
  );
};

export default GuestLandingPage;
